import React, { useState } from "react";
import SectionLabel from "../../components/SectionLabel";
import LawPill from "../../components/LawPill";
import RouteStrip from "../../components/RouteStrip";
import ReviewChat from "./ReviewChat";
import "./ComplaintLetter.css";

export default function ComplaintLetter({
  case: caseData,
  letter,
  checklist = [],
  legal_articles: legalArticles = [],
  review_path: reviewPath,
  regenerate_path: regeneratePath,
  next_steps_path: nextStepsPath,
  has_review_messages: hasReviewMessages = false,
}) {
  const [copied, setCopied] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const isClosed = Boolean(caseData.closed);
  const canReview = Boolean(letter?.body && reviewPath);
  const canRegenerate = Boolean(regeneratePath) && hasReviewMessages && !isClosed;
  const reviewLabel = isClosed ? "Visualizar Chat com Ana" : "Melhorar com IA";

  const handleCopy = async () => {
    if (!letter?.body) return;
    await navigator.clipboard.writeText(letter.body);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleRegenerate = async () => {
    if (!canRegenerate || regenerating || isClosed) return;
    setRegenerating(true);
    const meta = document.querySelector('meta[name="csrf-token"]');
    const initialUpdatedAt = letter?.updated_at ?? null;
    try {
      const response = await fetch(regeneratePath, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": meta ? meta.content : "",
        },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      pollForNewLetter(initialUpdatedAt);
    } catch {
      setRegenerating(false);
    }
  };

  const pollForNewLetter = (initialUpdatedAt) => {
    const url = window.location.pathname;
    const maxAttempts = 40; // ~2 minutes at 3s interval
    let attempts = 0;

    const tick = async () => {
      attempts += 1;
      try {
        const res = await fetch(url, {
          headers: { Accept: "application/json" },
          credentials: "same-origin",
        });
        if (res.ok) {
          const data = await res.json();
          const newUpdatedAt = data?.letter?.updated_at ?? null;
          if (newUpdatedAt && newUpdatedAt !== initialUpdatedAt) {
            window.location.reload();
            return;
          }
        }
      } catch {
        // ignore transient errors; keep polling
      }
      if (attempts < maxAttempts) {
        setTimeout(tick, 3000);
      } else {
        setRegenerating(false);
      }
    };

    setTimeout(tick, 3000);
  };

  return (
    <main
      className={`trunfo-letter${reviewOpen ? " trunfo-letter--review" : ""}${
        isClosed ? " trunfo-letter--closed" : ""
      }`}
    >
      {isClosed ? (
        <aside className="trunfo-letter__closed-banner" role="status">
          <SectionLabel tone="accent">Caso concluído</SectionLabel>
          <p>
            Este caso foi marcado como concluído. A carta está disponível apenas para leitura e o
            chat com a Ana fica em modo de visualização.
          </p>
        </aside>
      ) : null}
      <header className="trunfo-letter__top">
        <SectionLabel tone="mute">CASO {caseData.public_code} · MINUTA PRONTA</SectionLabel>
        <div className="trunfo-letter__top-row">
          <h1 className="trunfo-letter__title">
            {reviewOpen ? (
              isClosed ? (
                <>
                  Conversa arquivada, <em>com a Ana.</em>
                </>
              ) : (
                <>
                  Revisando sua carta, <em>com a Ana.</em>
                </>
              )
            ) : (
              <>
                Sua carta <em>formal</em>, revisada por IA.
              </>
            )}
          </h1>
          <div className="trunfo-letter__top-actions">
            <button type="button" className="trunfo-btn" onClick={handleCopy} disabled={!letter}>
              {copied ? "Copiado" : "Copiar texto"}
            </button>
            <button type="button" className="trunfo-btn" disabled>
              Exportar PDF
            </button>
          </div>
        </div>
      </header>

      {reviewOpen ? (
        <button type="button" className="trunfo-letter__back" onClick={() => setReviewOpen(false)}>
          ← Voltar à carta
        </button>
      ) : null}

      {reviewOpen ? (
        <div className="trunfo-letter__review-grid">
          <ReviewChat
            reviewPath={reviewPath}
            enabled={canReview && reviewOpen}
            onRegenerate={handleRegenerate}
            canRegenerate={canRegenerate}
            regenerating={regenerating}
            readOnly={isClosed}
          />

          <aside className="trunfo-letter__review-side">
            <section className="trunfo-letter__card">
              <SectionLabel tone="mute">Caso #{caseData.public_code}</SectionLabel>
              <h2 className="trunfo-letter__review-title">{caseData.title}</h2>
              {caseData.route?.origin && caseData.route?.destination ? (
                <RouteStrip
                  origin={caseData.route.origin}
                  destination={caseData.route.destination}
                  flightCode={caseData.route.flight_code}
                  meta={formatDate(caseData.happened_at)}
                />
              ) : null}
              {caseData.documents_count > 0 ? (
                <a className="trunfo-btn trunfo-btn--block" href={caseData.back_path}>
                  Ver {caseData.documents_count}{" "}
                  {caseData.documents_count === 1 ? "documento anexo" : "documentos anexos"}
                </a>
              ) : null}
            </section>

            {legalArticles.length > 0 ? (
              <section className="trunfo-letter__card">
                <SectionLabel tone="mute">Fundamentos legais citados</SectionLabel>
                <ul className="trunfo-letter__pills">
                  {legalArticles.map((a) => (
                    <li key={a.code}>
                      <LawPill code={a.code} href={a.url} />
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            <button
              type="button"
              className="trunfo-letter__cta"
              onClick={() => setReviewOpen(false)}
            >
              Voltar à carta formal →
            </button>
          </aside>
        </div>
      ) : (
        <div className="trunfo-letter__grid">
          <article className="trunfo-letter__paper" aria-label="Texto da reclamação">
            {canReview ? (
              <button
                type="button"
                className="trunfo-letter__ai-inline"
                onClick={() => setReviewOpen(true)}
                title={reviewLabel}
              >
                <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden>
                  <path
                    fill="currentColor"
                    d="M12 2l1.9 4.8L18.8 8.7l-4.9 1.9L12 15.4l-1.9-4.8L5.2 8.7l4.9-1.9L12 2z"
                  />
                </svg>
                <span>{reviewLabel}</span>
              </button>
            ) : null}
            {letter?.body ? (
              <pre className="trunfo-letter__body">{letter.body}</pre>
            ) : (
              <p className="trunfo-letter__empty">
                A carta ainda não foi gerada. Volte ao caso e construa os argumentos.
              </p>
            )}
          </article>

          <aside className="trunfo-letter__side">
            {checklist.length > 0 ? (
              <section className="trunfo-letter__card">
                <SectionLabel tone="mute">Checklist pré-envio</SectionLabel>
                <ul className="trunfo-letter__checklist">
                  {checklist.map((item) => (
                    <li key={item.key} className={`trunfo-letter__check ${item.ok ? "is-ok" : ""}`}>
                      <span className="trunfo-letter__check-mark" aria-hidden>
                        {item.ok ? "●" : "○"}
                      </span>
                      {item.label}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {legalArticles.length > 0 ? (
              <section className="trunfo-letter__card">
                <SectionLabel tone="mute">Bases legais</SectionLabel>
                <ul className="trunfo-letter__pills">
                  {legalArticles.map((a) => (
                    <li key={a.code}>
                      <LawPill code={a.code} title={a.title} href={a.url} />
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            <section className="trunfo-letter__card trunfo-letter__card--mute">
              <SectionLabel tone="mute">Próximos passos</SectionLabel>
              <p className="trunfo-letter__hint">
                Com a carta pronta, é hora de agir. Comece pela negociação amigável e, se não der
                certo, escale para <strong>consumidor.gov.br</strong>, <strong>Reclame Aqui</strong>
                , <strong>ANAC</strong> e, em último caso, <strong>Procon e JEC</strong>.
              </p>
              {nextStepsPath ? (
                <a className="trunfo-letter__cta trunfo-letter__cta--block" href={nextStepsPath}>
                  Abrir guia de ações →
                </a>
              ) : null}
            </section>

            <a className="trunfo-btn trunfo-btn--block" href={caseData.back_path}>
              Voltar ao caso
            </a>
          </aside>

          <nav className="trunfo-letter__mobile-bar" aria-label="Ações da carta">
            <a className="trunfo-letter__mobile-bar-back" href={caseData.back_path}>
              ← Caso
            </a>
            {nextStepsPath ? (
              <a className="trunfo-letter__mobile-bar-cta" href={nextStepsPath}>
                Próximos passos →
              </a>
            ) : null}
          </nav>
        </div>
      )}

      {regenerating ? (
        <div className="trunfo-letter__overlay" role="status" aria-live="polite">
          <div className="trunfo-letter__overlay-card">
            <span className="trunfo-letter__spinner" aria-hidden />
            <div>
              <strong>Regerando sua carta...</strong>
              <p>
                A Ana está revisando os argumentos com base na conversa. Isso leva alguns segundos.
              </p>
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
}

function formatDate(iso) {
  if (!iso) return undefined;
  try {
    return new Date(iso).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return undefined;
  }
}
